import React, { useEffect, useRef, useState } from 'react'
import ItemsRequestsApp from './ItemsRequestsApp'
import { IRequestedItem, ItemsRequestModel } from '.'
import { Button, Checkbox, FormControlLabel, Skeleton, Switch, TextField, Tooltip } from '@mui/material'
import SectionBoxContainer from '../../components/SectionBoxContainer'
import NothingFound from '../../components/NothingFound'
import { useDispatch } from 'react-redux'
import { handleRequestError } from '../../services/api'
import { QRCodeSVG } from 'qrcode.react'
import ApplicationLogo from '../../components/ApplicationLogo'
import StatusChip from '../../components/StatusChip'
import DateAndTime from '../DateAndTime'
import ApplicationConfig from '../../config'
import SelectRequestedItems from './SelectRequestedItems2'
import { Helmet } from 'react-helmet'
import { useReturnCurrentUser } from '../user/userSlice'
import { LoadingButton } from '@mui/lab'
import GenerateIcon from '../../components/GenerateIcon'
import RequestDesignation from './RequestDesignation'
import RequestDestination from './RequestDestination'
import AlertElement from '../../components/AlertElement'
import Breadcrumbs from '../../components/Breadcrumbs'
import RequestLog from './RequestLog'
import RequestDestinationAddress from './RequestDestinationAddress'
import Tabs, { ITab } from '../../components/Tabs'
import { updateQuickNotificationDetails } from '../notifications/notificationsSlice'
import RequestedMissingItems from './RequestedMissingItems'
import RequestIssues from './RequestIssues'
import ReturnedRequestedItems from './ReturnedRequestedItems'
import RequestTimeline from './RequestTimeline'
import ReguestDetailedDestination from './ReguestDetailedDestination'
import { saveAs } from "file-saver"
import DateAndTimeField from '../../components/Fields/DateAndTimeField'
import Attachments, { IAttachment } from '../../components/Attachments'
import ExportRequest from './ExportRequest'
import RequestedItemsList from './RequestedItemsList'

interface ISingleRequestProps {
    requestNumber: string,
    showHeadSection?: boolean,
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export default function SingleRequest(props: ISingleRequestProps) {

    const dispatch = useDispatch()

    const { requestNumber, showHeadSection } = props

    const lastRequestRef = useRef(null)

    const itemsRequestApp = new ItemsRequestsApp()

    const [request, setRequest] = useState<ItemsRequestModel | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [loadingItems, setLoadingItems] = useState<boolean>(true)

    const [requestedItems, setRequestedItems] = useState<IRequestedItem[]>([])

    const [reviewNote, setReviewNote] = useState<string>("")
    const [note, setNote] = useState<string>("")
    const [notifyByEmail, setNotifyByEmail] = useState<boolean>(true)

    const [customTimestamp, setCustomTimestamp] = useState<Date | null>(null)
    const [enableCustomTimestamp, setEnableCustomTimestamp] = useState<boolean>(false)

    const dateAndTime = new DateAndTime()

    const currentUser = useReturnCurrentUser()

    const [forceRefreshLog, setForceRefreshLog] = useState<boolean>(false)
    const [forceRefreshRequest, setForceRefreshRequest] = useState<boolean>(false)

    const [hideAllocations, setHideAllocations] = useState<boolean>(false)
    const [hideContainedItems, setHideContainedItems] = useState<boolean>(false)
    const [loadingExport, setLoadingExport] = useState<boolean>(false)

    const [ignoreReceivingUser, setIgnoreReceivingUser] = useState<boolean>(false)

    const [attachments, setAttachments] = useState<IAttachment[]>([])


    /**
     * Tabs
     */
    const [activeTab, setActiveTab] = useState<string>("details")
    const [availableTabs, setAvailableTabs] = useState<string[]>([
        "details",
        "updates",
        "manage",
        "issues",
        "destination",
        "export",
    ])

    /**
     * Load requested items
     */
    const loadRequestedItems = () => {
        setLoadingItems(true)

        itemsRequestApp.requstedItemsList(requestNumber)
            .then(response => {
                setRequestedItems(response.data)
                setLoadingItems(false)
            })
            .catch(error => {
                setLoadingItems(false)
                handleRequestError(error, dispatch)
            })
    }

    /**
     * Load Request
     */
    const loadRequest = () => {
        // setLoading(true)

        const requestUpdatedNotification = () => updateQuickNotificationDetails(true, "Request has been updated", "info", dispatch)

        itemsRequestApp.requestDetails(requestNumber)
            .then(response => {
                // setRequest(response.data)
                // setLoading(false)
                const newRequest = response.data

                if (forceRefreshRequest){
                    setForceRefreshRequest(false)

                    setRequest(newRequest)
                    requestUpdatedNotification()
                }else{
                    if (JSON.stringify(lastRequestRef.current) !== JSON.stringify(newRequest)) {
                        setRequest(newRequest)
                        lastRequestRef.current = newRequest
                        setForceRefreshLog(true)
    
                        requestUpdatedNotification()
                        setForceRefreshRequest(false)
                    }
                }

                setLoading(false)
            })
            .catch(error => {
                setLoading(false)
                setForceRefreshRequest(false)
                handleRequestError(error, dispatch)
            })
    }

    useEffect(() => {
        loadRequest()
        loadRequestedItems()

        const interval = setInterval(() => {
            // if (document.visibilityState === 'visible') {
            //   loadRequest()
            // }
            loadRequest()
        }, 10000)

        if (forceRefreshRequest){
            loadRequest()
            setForceRefreshRequest(false)
        }
        
        return () => clearInterval(interval)
    }, [requestNumber, forceRefreshRequest])

    if (loading){
        return(
            <div>
                <Skeleton variant="text" width={150} height={30} style={{ marginBottom: 10 }} />
                <Skeleton variant="text" width={200} height={20} style={{ marginBottom: 5 }}/>
                <Skeleton variant="text" width={250} height={20} style={{ marginBottom: 20 }}/>
                <Skeleton variant="rectangular" width={"100%"} height={300} style={{ marginBottom: 10 }} />
                <Skeleton variant="rectangular" width={"100%"} height={200} style={{ marginBottom: 5 }} />
            </div>
        )
    }

    if (!request){
        return(
            <SectionBoxContainer>
                <NothingFound title="Nothing found :(" message="Request doesn't exist." />
            </SectionBoxContainer>
        )
    }

    /**
     * Approve request
     */
    const approveRequest = (reject: boolean = false) => {
        if (request.status !== "pending"){
            return;
        }

        const status: string = reject ? "rejected" : "approved"

        setLoading(true)
        itemsRequestApp.approveItemsRequest(request.request_number, status, reviewNote, notifyByEmail, enableCustomTimestamp ? customTimestamp : null)
            .then(response => {
                setRequest(response.data.request)
                setLoading(false)
                setNote("")

                setEnableCustomTimestamp(false)
                setCustomTimestamp(null)
            })
            .catch(error => {
                setLoading(false)
                handleRequestError(error, dispatch)
            })

    }

    /**
     * Notify by email checkbox
     */
    const notifyByEmailCheckbox = (customTimestampBox?: boolean) => {
        return(
            <>
                {enableCustomTimestamp && (
                    <div className="request-manage-box-custom-timestamp">
                        <DateAndTimeField 
                            id="custom_timestamp"
                            label="Timestamp"
                            type="DateAndTime"
                            xs={12}
                            handleOnChange={(id: string, value: any) => setCustomTimestamp(value)}
                            data={{ name: "custom_timestamp", value: customTimestamp }}
                            fullWidth
                        />
                    </div>
                )}

                <FormControlLabel style={{ marginTop: 5 }} control={<Checkbox checked={notifyByEmail} onChange={(e) => setNotifyByEmail(e.target.checked)} />} label="Notify by email" />
                {customTimestampBox && <FormControlLabel style={{ marginTop: 5 }} control={<Checkbox checked={enableCustomTimestamp} onChange={(e) => setEnableCustomTimestamp(e.target.checked)} />} label="Custom timestamp" />}
            </>
        )
    }

    /**
     * Render note
     */
    const renderNote = (customTimestampBox?: boolean) => {
        return(
            <div>

                <TextField 
                    variant="outlined" 
                    label="Note" 
                    value={note} 
                    onChange={(e) => setNote(e.target.value)} 
                    fullWidth 
                    multiline
                    rows={4}
                    style={{ marginBottom: 10 }}
                />

                <Attachments attachments={attachments} setAttachments={setAttachments} label="Images" maxAttachments={10} />

                {notifyByEmailCheckbox(customTimestampBox)}

            </div>
        )
    }

    /**
     * Review request
     */
    const reviewRequest = () => {

        return(
            <div>
                <TextField 
                    variant="outlined" 
                    label="Review note" 
                    value={reviewNote} 
                    onChange={(e) => setReviewNote(e.target.value)} 
                    fullWidth 
                    multiline
                    rows={4}
                />

                {notifyByEmailCheckbox(true)}

                <div className="action-buttons-of-section-box-admin-panel items-requests">
                    <div className="content-of-action-buttons-of-section-box-admin-panel">
                        <LoadingButton loading={loading} className="general-button admin-style light-button" color="success" variant="contained" startIcon={<GenerateIcon icon="success" />} onClick={() => approveRequest()}>Approve Request</LoadingButton>
                        <LoadingButton loading={loading} className="general-button admin-style light-button" color="error" variant="contained" startIcon={<GenerateIcon icon="close" />} onClick={() => approveRequest(true)}>Decline Request</LoadingButton>
                    </div>
                </div>
            </div>
        )
    }

    /**
     * Move items
     */
    const moveItems = () => {

        let movedTitle: string = "temporarily moved"
        let buttonText: string = "Temprorarily Move Items"

        if (request.action_type === "move"){
            movedTitle = "permanently moved"
            buttonText = "Permanently Move Items"
        }

        return(
            <div>
                
                {renderNote(true)}

                <AlertElement type="info" message={`The items have been preapred and are ready to be ${movedTitle} to the request destination.`} />

                <div className="action-buttons-of-section-box-admin-panel items-requests">
                    <div className="content-of-action-buttons-of-section-box-admin-panel">
                        <LoadingButton loading={loading} className="general-button admin-style light-button" color="success" variant="contained" startIcon={<GenerateIcon icon="move" />} onClick={() => prepareItemsForShipment(false, true)}>{buttonText}</LoadingButton>
                    </div>
                </div>

            </div>
        )
    }

    /**
     * Prepare items for shipment
     */
    const prepareItemsForShipment = (done: boolean = false, in_transit: boolean = false) => {
        setLoading(true)
        itemsRequestApp.prepareItemsForTransit(request.request_number, done, in_transit, note, notifyByEmail, enableCustomTimestamp ? customTimestamp : null, attachments)
            .then(response => {
                setRequest(response.data.request)
                setLoading(false)
                setNote("")
                setActiveTab("details")

                setEnableCustomTimestamp(false)
                setCustomTimestamp(null)
                setAttachments([])
            })
            .catch(error => {
                setLoading(false)
                handleRequestError(error, dispatch)
            })
    }

    /**
     * Prepare Items
     */
    const prepareItems = () => {
        return(
            <div>

                {renderNote(true)}

                <AlertElement type="info" message={`Request has been approved. Waiting for the items to be prepared`} />

                <div className="action-buttons-of-section-box-admin-panel items-requests">
                    <div className="content-of-action-buttons-of-section-box-admin-panel">
                        <LoadingButton loading={loading} className="general-button admin-style light-button" color="success" variant="contained" startIcon={<GenerateIcon icon="inventory" />} onClick={() => prepareItemsForShipment()}>Start Preparing Items</LoadingButton>
                    </div>
                </div>

            </div>
        )
    }

    /**
     * Handle fulfill request
     */
    const handleFullfillRequest = () => {
        setLoading(true)
        itemsRequestApp.fulfillRequest(request.request_number, note, notifyByEmail, enableCustomTimestamp ? customTimestamp : null, ignoreReceivingUser, attachments)
            .then(response => {
                setRequest(response.data.request)
                setLoading(false)
                setNote("")
                setActiveTab("details")

                setEnableCustomTimestamp(false)
                setCustomTimestamp(null)
                setIgnoreReceivingUser(false)

                setAttachments([])
            })
            .catch(error => {
                setLoading(false)
                handleRequestError(error, dispatch)
            })
    }

    /**
     * Render ignore receiving user
     */
    const renderIgnoreReceivingUser = (customLabel?: string) => {
        if (currentUser.is_superuser || currentUser.is_daemon){
            return <FormControlLabel style={{ marginTop: 5 }} control={<Checkbox checked={ignoreReceivingUser} onChange={(e) => setIgnoreReceivingUser(e.target.checked)} />} label={customLabel ? customLabel : "Receive items on behalf of user"} />
        }

        return <></>
    }

    /**
     * Receive items
     */
    const receiveItems = () => {

        let disabled: boolean = false
        let person: string = request.request_by_name

        if (request.designation_type == "user"){
            if (request.destination_user !== currentUser.id){
                disabled = true
                person = request.destination_user_name
            }
        }else{
            if (currentUser.id !== request.requested_by){
                disabled = true
            }
        }

        if (ignoreReceivingUser){
            disabled = false
        }

        return(
            <div>

                {renderNote(true)}

                <AlertElement type="info" message={`Items are currently on the way to the destination.`} />

                {disabled && <div style={{ marginTop: 10 }}><AlertElement type="error" message={`Only '${person}' can recieve the items.`} /></div>}

                <div className="action-buttons-of-section-box-admin-panel items-requests">
                    <div className="content-of-action-buttons-of-section-box-admin-panel">
                        {renderIgnoreReceivingUser()}
                        <LoadingButton disabled={disabled} loading={loading} className="general-button admin-style light-button" color="success" variant="contained" startIcon={<GenerateIcon icon="success" />} onClick={() => handleFullfillRequest()}>Items Received</LoadingButton>
                    </div>
                </div>

            </div>
        )
    }

    /**
     * Handle close request
     */
    const handleCloseRequest = () => {
        setLoading(true)
        itemsRequestApp.closeItemsRequest(request.request_number, note, notifyByEmail, enableCustomTimestamp ? customTimestamp : null, attachments)
            .then(response => {
                setRequest(response.data.request)
                setLoading(false)
                setNote("")
                setActiveTab("details")

                setEnableCustomTimestamp(false)
                setCustomTimestamp(null)

                setAttachments([])
            })
            .catch(error => {
                setLoading(false)
                handleRequestError(error, dispatch)
            })
    }

    /**
     * Close request
     */
    const completeRequest = () => {
        return(
            <div>

                {renderNote(true)}

                <div className="action-buttons-of-section-box-admin-panel items-requests">
                    <div className="content-of-action-buttons-of-section-box-admin-panel">
                        <LoadingButton loading={loading} className="general-button admin-style light-button" color="success" variant="contained" startIcon={<GenerateIcon icon="success" />} onClick={() => handleCloseRequest()}>Close Request</LoadingButton>
                    </div>
                </div>

            </div>
        )
    }

    /**
     * Done preparing items
     */
    const donePreparingItems = () => {
        return(
            <div>

                {renderNote(true)}

                <AlertElement type="info" message={`Items are being preapred.`} />

                <div className="action-buttons-of-section-box-admin-panel items-requests">
                    <div className="content-of-action-buttons-of-section-box-admin-panel">
                        <LoadingButton loading={loading} className="general-button admin-style light-button" color="success" variant="contained" startIcon={<GenerateIcon icon="success" />} onClick={() => prepareItemsForShipment(true)}>Complete Preparing Items</LoadingButton>
                    </div>
                </div>

            </div>
        )
    }

    /**
     * Handle return items
     */
    const handleReturnItems = (received?: boolean) => {
        setLoading(true)
        itemsRequestApp.returnRequestedItems(request.request_number, note, received, 0, false, notifyByEmail, enableCustomTimestamp ? customTimestamp : null, attachments)
            .then(response => {
                setRequest(response.data.request)
                setLoading(false)
                setNote("")
                setActiveTab("details")

                setEnableCustomTimestamp(false)
                setCustomTimestamp(null)

                setAttachments([])
            })
            .catch(error => {
                setLoading(false)
                handleRequestError(error, dispatch)
            })
    }

    /**
     * Return items
     */
    const returnItemsBack = () => {

        let userName: string = request.request_by_name

        if (request.designation_type === "user"){
            userName = request.destination_user_name
        }

        let message: string = `Items have been sent back to the origin by ${userName}, and are currently on the way back.`

        let disabled: boolean = false


        return(
            <div>

                {renderNote(true)}

                <AlertElement type="info" message={message} />

                <div className="action-buttons-of-section-box-admin-panel items-requests">
                    <div className="content-of-action-buttons-of-section-box-admin-panel">
                        {/* {renderIgnoreReceivingUser()} */}
                        <LoadingButton disabled={disabled} loading={loading} className="general-button admin-style light-button" color="success" variant="contained" startIcon={<GenerateIcon icon="move" />} onClick={() => handleReturnItems(true)}>Received Items</LoadingButton>
                    </div>
                </div>

            </div>
        )
    }

    /**
     * Handle send items
     */
    const handleSendItemsBack = () => {
        setLoading(true)
        itemsRequestApp.sendBackRequestedItems(request.request_number, note, notifyByEmail, enableCustomTimestamp ? customTimestamp : null, ignoreReceivingUser, attachments)
            .then(response => {
                setRequest(response.data.request)
                setLoading(false)
                setNote("")
                setActiveTab("details")

                setEnableCustomTimestamp(false)
                setCustomTimestamp(null)
                setIgnoreReceivingUser(false)

                setAttachments([])
            })
            .catch(error => {
                setLoading(false)
                handleRequestError(error, dispatch)
            })
    }

    /**
     * Send items back
     */
    const sendItemsBack = () => {

        let disabled: boolean = false

        if (request.designation_type == "user"){
            if (request.destination_user !== currentUser.id){
                disabled = true
            }
        }else{
            if (currentUser.id !== request.requested_by){
                disabled = true
            }
        }

        if (ignoreReceivingUser){
            disabled = false
        }

        return(
            <div>

                {renderNote(true)}

                <div className="action-buttons-of-section-box-admin-panel items-requests">
                    <div className="content-of-action-buttons-of-section-box-admin-panel">
                        {renderIgnoreReceivingUser("Send items back on behalf of user")}
                        <LoadingButton disabled={disabled} loading={loading} className="general-button admin-style light-button" color="success" variant="contained" startIcon={<GenerateIcon icon="move" />} onClick={() => handleSendItemsBack()}>Send Items Back</LoadingButton>
                    </div>
                </div>

            </div>
        )
    }

    /**
     * Admin tool
     */
    const currentAdminTool = () => {
        if (request.status === "pending"){
            return reviewRequest()
        }

        if (request.status === "approved"){
            return prepareItems()
        }

        if (request.status === "preparing"){
            return donePreparingItems()
        }

        if (request.status === "ready"){
            return moveItems()
        }

        if (request.status === "transit"){
            return receiveItems()
        }
    
        if (request.status === "fulfilled"){
            return sendItemsBack()
        }

        if (request.status === "returning"){
            return returnItemsBack()
        }

        if (request.status === "returned"){
            return completeRequest()
        }

        return <></>
    }

    /**
     * Admin tools
     */
    const renderAdminTools = () => {


        const nothingToManage = () => {
            return(
                <SectionBoxContainer
                    showTitle
                    title="Manage Request"
                >
                    <AlertElement type={"info"} message="Nothing to manage at this moment." />
                </SectionBoxContainer>
            )
        }

        const adminTools = () => {
            return(
                <div className="admin-tools-for-item-request">
                    <SectionBoxContainer
                        showTitle
                        title="Manage Request"
                    >
                        {currentAdminTool()}
                    </SectionBoxContainer>
                </div>
            )
        }

        if (!request){
            return <></>
        }

        if (request.status == "transit" || request.status == "fulfilled"){
            return adminTools()
        }else{
            if (!currentUser.is_superuser){
                return nothingToManage()
            }
        }

        if (request.status == "rejected" || request.status == "closed"){
            return nothingToManage()
        }


        return adminTools()
    }

    let reviewedText: string = "Reviewed"

    if (request.status === "approved" || request.status === "fulfilled" || request.status === "closed" || request.status === "preparing" || request.status === "ready" || request.status === "transit"){
        reviewedText = "Approved"
    }

    if (request.status === "rejected"){
        reviewedText = "Rejected"
    }

    /**
     * Render destination details
     */
    const renderDestinationDetails = () => {

        // if (request.designation_type === "user"){
        //     return <></>
        // }

        let receivingBy: string = request.request_by_name

        if (request.designation_type === "user"){
            receivingBy = request.destination_user_name
        }

        return(
            <div className="destination-details-of-items-request">
                                    <div className="items-request-details-box">
                                        <div className="content-of-items-request-details-box">

                                            <div className="header-of-items-request-details-box">
                                                <div className="content-of-header-of-items-request-details-box">
                                                    <h3>Items Destination</h3>
                                                </div>
                                            </div>

                                            <div className="fields-of-items-request-details-box">
                                                <div className="content-of-fields-of-items-request-details-box">

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>Destiation:</h3>
                                                            <RequestDestination request={request} />
                                                        </div>
                                                    </div>

                                                    {request.designation_type !== "user" && (
                                                        <div className="field-of-items-request-details-box">
                                                            <div className="content-of-field-of-items-request-details-box">
                                                                <h3>Address:</h3>
                                                                <RequestDestinationAddress request={request} />
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>{request.received_at ? "Received by:" : "Receiving by:"}</h3>
                                                            <h4>{receivingBy}</h4>
                                                        </div>
                                                    </div>

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>Received on:</h3>
                                                            <h4>{request.received_at ? dateAndTime.formatDateAndTime(request.received_at) : "-"}</h4>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>

                                        </div>
                                    </div>

            </div>
        )
    }

    /**
     * Handle export items to excel
     */
    const handleExportRequestedItems = () => {

        setLoadingExport(true)

        itemsRequestApp.exportRequestedItems(request.request_number)
            .then(response => {
                setLoadingExport(false)
    
                const blob = response.data;
    
                const contentDisposition = response.headers["content-disposition"]
                let fileName = "exported_items.xlsx"

                console.log("contentDisposition", contentDisposition)
                console.log('response.headers', response.headers, response)

                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="(.+)"/)
                    if (match && match[1]) {
                        fileName = match[1]
                    }
                }
    
                saveAs(blob, fileName)
    
                updateQuickNotificationDetails(true, "The requested items have been exported successfully!", "success", dispatch);
            })
            .catch(error => {
                setLoadingExport(false)
                handleRequestError(error, dispatch);
            }); 

    }

    /**
     * Render request details
     */
    const renderRequestDetails = () => {
        return(
            <>
            <div className="new-single-items-request-page-container">
                <div className="content-of-new-single-items-request-page-container">

                    <div className="info-of-single-items-request-page-container">
                        <div className="content-of-info-of-single-items-request-page-container">

                            <h1>{request.request_number}</h1>

                            <div className="items-request-details-boxes">
                                <div className="content-of-items-request-details-boxes">

                                    <div className="items-request-details-box">
                                        <div className="content-of-items-request-details-box">

                                            <div className="header-of-items-request-details-box">
                                                <div className="content-of-header-of-items-request-details-box">
                                                    <h3>Request Status</h3>
                                                </div>
                                            </div>

                                            <div className="fields-of-items-request-details-box">
                                                <div className="content-of-fields-of-items-request-details-box">

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>Status:</h3>
                                                            <StatusChip type={request.status} />
                                                        </div>
                                                    </div>

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>{reviewedText} on:</h3>
                                                            <h4>{request.reviewed_at ? dateAndTime.formatDateAndTime(request.reviewed_at) : "-"}</h4>
                                                        </div>
                                                    </div>

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>{reviewedText} by:</h3>
                                                            <h4>{request.reviewed_by_name ? request.reviewed_by_name : "-"}</h4>
                                                        </div>
                                                    </div>

                                                    <div className="field-of-items-request-details-box note">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>{reviewedText} note:</h3>
                                                            <h4>{request.review_note ? request.review_note : "-"}</h4>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>

                                        </div>
                                    </div>

                                    <div className="items-request-details-box">
                                        <div className="content-of-items-request-details-box">

                                            <div className="header-of-items-request-details-box">
                                                <div className="content-of-header-of-items-request-details-box">
                                                    <h3>Request Overview</h3>
                                                </div>
                                            </div>

                                            <div className="fields-of-items-request-details-box">
                                                <div className="content-of-fields-of-items-request-details-box">

                                                    {request.title !== request.request_number && (
                                                        <div className="field-of-items-request-details-box">
                                                            <div className="content-of-field-of-items-request-details-box">
                                                                <h3>Request Title:</h3>
                                                                <h4>{request.title}</h4>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>Request Type:</h3>
                                                            <h4><StatusChip type={request.action_type} /></h4>
                                                        </div>
                                                    </div>

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>Category:</h3>
                                                            <h4><StatusChip type={request.category} /></h4>
                                                        </div>
                                                    </div>

                                                    {/* <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>Destination:</h3>
                                                            <RequestDestination request={request} />
                                                        </div>
                                                    </div> */}

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>Project:</h3>
                                                            <h4>{request.project_title ? request.project_title : "-"}</h4>
                                                        </div>
                                                    </div>

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>Requested by:</h3>
                                                            <h4>{request.request_by_name}</h4>
                                                        </div>
                                                    </div>

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>Requested on:</h3>
                                                            <h4>{request.created_at ? dateAndTime.formatDateAndTime(request.created_at) : "-"}</h4>
                                                        </div>
                                                    </div>

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>Start on:</h3>
                                                            <h4>{request.start_date ? dateAndTime.formatDate(request.start_date) : "-"}</h4>
                                                        </div>
                                                    </div>

                                                    <div className="field-of-items-request-details-box">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>Return by:</h3>
                                                            <h4>{request.return_by_date ? dateAndTime.formatDate(request.return_by_date) : "-"}</h4>
                                                        </div>
                                                    </div>

                                                    <div className="field-of-items-request-details-box note">
                                                        <div className="content-of-field-of-items-request-details-box">
                                                            <h3>Note:</h3>
                                                            <h4>{request.note ? request.note : "-"}</h4>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>

                                        </div>
                                    </div>

                                </div>
                            </div>

                            {renderDestinationDetails()}

                        </div>
                    </div>

                    <RequestedItemsList 
                        loadingItems={loadingItems} 
                        hideAllocations={hideAllocations} 
                        hideContainedItems={hideContainedItems} 
                        forceRefreshRequest={(status) => setForceRefreshRequest(status)} 
                        request={request} 
                        items={requestedItems} 
                        setItems={(items) => setRequestedItems(items)}  
                        viewRequestPage={true} 
                        renderAfterTabs={() => {
                            return (
                                <div className="requsted-items-of-single-items-request-page-container">
                                    {request && request.status !== "pending" && (
                                        <div className="controls-of-requsted-items-of-single-items-request-page-container hide-for-print">
                                            <div className="content-of-controls-of-requsted-items-of-single-items-request-page-container">
                                                <FormControlLabel control={<Switch checked={hideAllocations} onClick={(e) => setHideAllocations(hideAllocations ? false : true)} />} label="Hide Allocations" />
                                                <FormControlLabel control={<Switch checked={hideContainedItems} onClick={(e) => setHideContainedItems(hideContainedItems ? false : true)} />} label="Hide Contained Items" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        }}
                    />

                </div>
            </div>

            </>
        )
    }

    // const old_renderRequestDetails = () => {
    //     return(
    //         <>

    //             {/* <div className="action-buttons-of-section-box-admin-panel items-requests header-view hide-for-print">
    //                 <div className="content-of-action-buttons-of-section-box-admin-panel">

    //                     <Button className="general-button admin-style" color="success" variant="text" startIcon={<GenerateIcon icon="print" />} onClick={() => window.print()}>Print</Button>
                        
    //                     <Tooltip title="Export requested items to .xls">
    //                         <LoadingButton loading={loadingExport} className="general-button admin-style" variant="text" startIcon={<GenerateIcon icon="download" />} onClick={() => handleExportRequestedItems()}>Export to Excel</LoadingButton>
    //                     </Tooltip>
    //                 </div>
    //             </div> */}

    //         <div className="single-items-request-page-container">
    //             <div className="content-of-single-items-request-page-container">
                    
    //                 <div className="header-of-single-items-request-page-container">
    //                     <div className="content-of-header-of-single-items-request-page-container">
                            
    //                         <div className="logo-items-request-header">
    //                             <div className="content-of-logo-items-request-header">
    //                                 <ApplicationLogo />
    //                             </div>
    //                         </div>

    //                         <div className="items-request-header-qr-code">
    //                             <div className="content-of-items-request-header-qr-code">
    //                                 <QRCodeSVG value={ApplicationConfig.appBaseUrl + "/requests/" +request.request_number} size={256} level="H" />
    //                             </div>
    //                         </div>

    //                     </div>
    //                 </div>

    //                 <div className="info-of-single-items-request-page-container">
    //                     <div className="content-of-info-of-single-items-request-page-container">

    //                         <h1>{request.request_number}</h1>

    //                         <div className="items-request-details-boxes">
    //                             <div className="content-of-items-request-details-boxes">

    //                                 <div className="items-request-details-box">
    //                                     <div className="content-of-items-request-details-box">

    //                                         <div className="header-of-items-request-details-box">
    //                                             <div className="content-of-header-of-items-request-details-box">
    //                                                 <h3>Request Status</h3>
    //                                             </div>
    //                                         </div>

    //                                         <div className="fields-of-items-request-details-box">
    //                                             <div className="content-of-fields-of-items-request-details-box">

    //                                                 <div className="field-of-items-request-details-box">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>Status:</h3>
    //                                                         <StatusChip type={request.status} />
    //                                                     </div>
    //                                                 </div>

    //                                                 <div className="field-of-items-request-details-box">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>{reviewedText} on:</h3>
    //                                                         <h4>{request.reviewed_at ? dateAndTime.formatDateAndTime(request.reviewed_at) : "-"}</h4>
    //                                                     </div>
    //                                                 </div>

    //                                                 <div className="field-of-items-request-details-box">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>{reviewedText} by:</h3>
    //                                                         <h4>{request.reviewed_by_name ? request.reviewed_by_name : "-"}</h4>
    //                                                     </div>
    //                                                 </div>

    //                                                 <div className="field-of-items-request-details-box note">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>{reviewedText} note:</h3>
    //                                                         <h4>{request.review_note ? request.review_note : "-"}</h4>
    //                                                     </div>
    //                                                 </div>

    //                                             </div>
    //                                         </div>

    //                                     </div>
    //                                 </div>

    //                                 <div className="items-request-details-box">
    //                                     <div className="content-of-items-request-details-box">

    //                                         <div className="header-of-items-request-details-box">
    //                                             <div className="content-of-header-of-items-request-details-box">
    //                                                 <h3>Request Overview</h3>
    //                                             </div>
    //                                         </div>

    //                                         <div className="fields-of-items-request-details-box">
    //                                             <div className="content-of-fields-of-items-request-details-box">

    //                                                 <div className="field-of-items-request-details-box">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>Request Type:</h3>
    //                                                         <h4><StatusChip type={request.action_type} /></h4>
    //                                                     </div>
    //                                                 </div>

    //                                                 <div className="field-of-items-request-details-box">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>Category:</h3>
    //                                                         <h4><StatusChip type={request.category} /></h4>
    //                                                     </div>
    //                                                 </div>

    //                                                 {/* <div className="field-of-items-request-details-box">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>Destination:</h3>
    //                                                         <RequestDestination request={request} />
    //                                                     </div>
    //                                                 </div> */}

    //                                                 <div className="field-of-items-request-details-box">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>Project:</h3>
    //                                                         <h4>{request.project_title ? request.project_title : "-"}</h4>
    //                                                     </div>
    //                                                 </div>

    //                                                 <div className="field-of-items-request-details-box">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>Requested by:</h3>
    //                                                         <h4>{request.request_by_name}</h4>
    //                                                     </div>
    //                                                 </div>

    //                                                 <div className="field-of-items-request-details-box">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>Requested on:</h3>
    //                                                         <h4>{request.created_at ? dateAndTime.formatDateAndTime(request.created_at) : "-"}</h4>
    //                                                     </div>
    //                                                 </div>

    //                                                 <div className="field-of-items-request-details-box">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>Start on:</h3>
    //                                                         <h4>{request.start_date ? dateAndTime.formatDate(request.start_date) : "-"}</h4>
    //                                                     </div>
    //                                                 </div>

    //                                                 <div className="field-of-items-request-details-box">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>Return by:</h3>
    //                                                         <h4>{request.return_by_date ? dateAndTime.formatDate(request.return_by_date) : "-"}</h4>
    //                                                     </div>
    //                                                 </div>

    //                                                 <div className="field-of-items-request-details-box note">
    //                                                     <div className="content-of-field-of-items-request-details-box">
    //                                                         <h3>Note:</h3>
    //                                                         <h4>{request.note ? request.note : "-"}</h4>
    //                                                     </div>
    //                                                 </div>

    //                                             </div>
    //                                         </div>

    //                                     </div>
    //                                 </div>

    //                             </div>
    //                         </div>

    //                         {renderDestinationDetails()}

    //                     </div>
    //                 </div>

    //                 {request && <RequestedMissingItems request={request} items={requestedItems} loadingItems={loadingItems} />}

    //                 {request && <RequestedMissingItems request={request} items={requestedItems} loadingItems={loadingItems} showDamaged />}

    //                 {request && <RequestedMissingItems request={request} items={requestedItems} loadingItems={loadingItems} showRejected />}

    //                 {request && <ReturnedRequestedItems request={request} items={requestedItems} loadingItems={loadingItems} />}


    //                 <div className="requsted-items-of-single-items-request-page-container">
    //                     {request && request.status !== "pending" && <h3 className="requested-items-title">Requested Items ({requestedItems.length})</h3>}

    //                     {request && request.status !== "pending" && (
    //                         <div className="controls-of-requsted-items-of-single-items-request-page-container hide-for-print">
    //                             <div className="content-of-controls-of-requsted-items-of-single-items-request-page-container">
    //                                 <FormControlLabel control={<Switch checked={hideAllocations} onClick={(e) => setHideAllocations(hideAllocations ? false : true)} />} label="Hide Allocations" />
    //                                 <FormControlLabel control={<Switch checked={hideContainedItems} onClick={(e) => setHideContainedItems(hideContainedItems ? false : true)} />} label="Hide Contained Items" />
    //                             </div>
    //                         </div>
    //                     )}

    //                     {loadingItems ? (
    //                         <>
    //                             <Skeleton variant="rectangular" width={"100%"} height={100} style={{ marginBottom: 10 }}/>
    //                             <Skeleton variant="rectangular" width={"100%"} height={100} style={{ marginBottom: 10 }}/>
    //                             <Skeleton variant="rectangular" width={"100%"} height={100} style={{ marginBottom: 10 }}/>
    //                             <Skeleton variant="rectangular" width={"100%"} height={100} style={{ marginBottom: 10 }}/>
    //                             <Skeleton variant="rectangular" width={"100%"} height={100} style={{ marginBottom: 10 }}/>
    //                             <Skeleton variant="rectangular" width={"100%"} height={100} style={{ marginBottom: 10 }}/>
    //                         </>
    //                     ) : <SelectRequestedItems hideAllocations={hideAllocations} hideContainedItems={hideContainedItems} forceRefreshRequest={(status) => setForceRefreshRequest(status)} request={request} items={requestedItems} setItems={(items) => setRequestedItems(items)}  viewRequestPage={true} />}
    //                 </div>

    //             </div>
    //         </div>

    //         </>
    //     )
    // }

    /**
     * Render Request updates
     */
    const renderRequestUpdates = () => {
        if (!requestNumber){
            return <></>
        }

        return(
            <div>

                {request.status !== "rejected" && <RequestTimeline request={request} />}

                <RequestLog requestNumber={requestNumber} forceRefreshStatus={forceRefreshLog} forceRefresh={(status) => setForceRefreshLog(status)} forceRefreshRequest={(status) => setForceRefreshRequest(status)} />

            </div>
        )
    }

    /**
     * Render Request Issues
     */
    const renderRequestIssues = () => {

        return(
            <SectionBoxContainer
                showTitle
                title="Issues"
                description="Here you can find all the issues that relate to the request items."
            >
                <RequestIssues request={request} forceRefreshStatus={forceRefreshLog} forceRefresh={(status) => setForceRefreshLog(status)} forceRefreshRequest={(status) => setForceRefreshRequest(status)} />
            </SectionBoxContainer>
        )
    }

    /**
     * Render Export Request Details
     */
    const renderExportRequestDetails = () => {

        return(
            <SectionBoxContainer
                showTitle
                title="Export Request"
                description="Here you can export request details to a pdf file."
            >
                
                <div className="export-request-to-excel">
                    <div className="action-buttons-of-section-box-admin-panel items-requests header-view hide-for-print">
                        <div className="content-of-action-buttons-of-section-box-admin-panel">                            
                            <Tooltip title="Export requested items to .xls">
                                <LoadingButton loading={loadingExport} className="general-button admin-style" variant="text" startIcon={<GenerateIcon icon="download" />} onClick={() => handleExportRequestedItems()}>Export to Excel</LoadingButton>
                            </Tooltip>
                        </div>
                    </div>
                </div>

                <ExportRequest requestNumber={requestNumber} />

            </SectionBoxContainer>
        )
    }

    /**
     * Render destination details
     */
    const renderRequestDestinationDetails = () => {

        return <ReguestDetailedDestination request={request} />
    }

    return(
        <div className="single-item-request-page-container">

            <Helmet>
                <title>{requestNumber} - {ApplicationConfig.appName}</title>
            </Helmet>

            {showHeadSection && (
                <div className="page-container-head-box" style={{ marginBottom: 35 }}>
                    <div className="content-of-page-container-head-box">
                        <h1>{request.request_number}</h1>

                        <Breadcrumbs breadcrumbs={[
                            {
                                title: "Home",
                                link: "/"
                            },
                            {
                                title: "My Requests",
                                link: "/requests"
                            },
                            {
                                title: request.request_number
                            }
                        ]} />
                    </div>
                </div>
            )}

            <Tabs 
                activeTab={activeTab}
                updateActivateTab={(name) => setActiveTab(name)}
                availableTabs={availableTabs}
                tabs={[
                    {
                        name: "details",
                        title: "Request Details",
                        icon: "list",
                        component: renderRequestDetails,
                    },
                    {
                        name: "destination",
                        title: "Destination",
                        icon: "map",
                        component: renderRequestDestinationDetails,
                    },
                    {
                        name: "updates",
                        title: "Updates",
                        icon: "refresh",
                        component: renderRequestUpdates,
                    },
                    {
                        name: "issues",
                        title: "Issues",
                        icon: "error",
                        component: renderRequestIssues,
                    },
                    {
                        name: "export",
                        title: "Export Request",
                        icon: "print",
                        component: renderExportRequestDetails
                    },
                    {
                        name: "manage",
                        title: "Manage Request",
                        icon: "settings",
                        component: renderAdminTools,
                    }
                ]}
            />


        </div>
    )
}